import {
  action,
  computed,
  observable,
} from "mobx";
import { SyncErrorKind } from "../../sync/SyncDomainController";

export const NutritionEntitySaveMode = {
  MANUAL: "manual",
  DEBOUNCED: "debounced",
};

export const NutritionEntitySyncStatus = {
  CLEAN: "clean",
  DIRTY: "dirty",
  SAVING: "saving",
  OFFLINE_PENDING: "offline_pending",
  ERROR: "error",
};

function clearTimer(timerId) {
  if (timerId != null) {
    clearTimeout(timerId);
  }
}

const DEFAULT_COMPARABLE_PAYLOAD_OMITTED_KEYS = Object.freeze([
  "schema_version",
  "_rev",
  "createdAt",
  "updatedAt",
]);

export function clonePlainValue(value) {
  if (value == null) {
    return value;
  }

  if (typeof structuredClone === "function") {
    try {
      return structuredClone(value);
    } catch (error) {
      // Fallback to JSON when observable data cannot be cloned directly.
    }
  }

  return JSON.parse(JSON.stringify(value));
}

export function buildComparablePayload(value, omittedKeys = DEFAULT_COMPARABLE_PAYLOAD_OMITTED_KEYS) {
  const clonedValue = clonePlainValue(value);
  if (
    clonedValue == null ||
    typeof clonedValue !== "object" ||
    Array.isArray(clonedValue)
  ) {
    return clonedValue;
  }

  (Array.isArray(omittedKeys) ? omittedKeys : []).forEach((key) => {
    delete clonedValue[key];
  });

  return clonedValue;
}

function areEqualByJson(leftValue, rightValue) {
  return JSON.stringify(leftValue ?? null) === JSON.stringify(rightValue ?? null);
}

function uniquePaths(paths) {
  return Array.from(
    new Set(
      (Array.isArray(paths) ? paths : [])
        .map((path) => String(path ?? "").trim())
        .filter(Boolean),
    ),
  );
}

export function diffTopLevelPaths(currentValue, nextValue) {
  const currentRecord =
    currentValue && typeof currentValue === "object" && !Array.isArray(currentValue)
      ? currentValue
      : {};
  const nextRecord =
    nextValue && typeof nextValue === "object" && !Array.isArray(nextValue)
      ? nextValue
      : {};

  return uniquePaths(
    Array.from(
      new Set([
        ...Object.keys(currentRecord),
        ...Object.keys(nextRecord),
      ]),
    ).filter((key) => !areEqualByJson(currentRecord[key], nextRecord[key])),
  );
}

export function mergeServerIntoLocalByTouchedPaths(
  serverSnapshot,
  localSnapshot,
  touchedPaths,
) {
  const baseServer =
    serverSnapshot && typeof serverSnapshot === "object" && !Array.isArray(serverSnapshot)
      ? serverSnapshot
      : {};
  const baseLocal =
    localSnapshot && typeof localSnapshot === "object" && !Array.isArray(localSnapshot)
      ? localSnapshot
      : {};
  const touched = new Set(uniquePaths(touchedPaths));
  const mergedSnapshot = clonePlainValue(baseServer) ?? {};

  Object.keys(baseLocal).forEach((key) => {
    if (!(key in mergedSnapshot) || touched.has(key)) {
      mergedSnapshot[key] = clonePlainValue(baseLocal[key]);
    }
  });

  return mergedSnapshot;
}

export function mergeServerIntoLocalByTouchedPathsPreservingRefs(
  serverSnapshot,
  localSnapshot,
  touchedPaths,
) {
  const baseServer =
    serverSnapshot && typeof serverSnapshot === "object" && !Array.isArray(serverSnapshot)
      ? serverSnapshot
      : {};
  const baseLocal =
    localSnapshot && typeof localSnapshot === "object" && !Array.isArray(localSnapshot)
      ? localSnapshot
      : {};
  const touched = new Set(uniquePaths(touchedPaths));
  const mergedSnapshot = clonePlainValue(baseServer) ?? {};

  Object.keys(baseLocal).forEach((key) => {
    if (!(key in mergedSnapshot) || touched.has(key)) {
      mergedSnapshot[key] = touched.has(key)
        ? baseLocal[key]
        : clonePlainValue(baseLocal[key]);
    }
  });

  return mergedSnapshot;
}

export class OptimisticNutritionEntity {
  _rev = 0;
  syncStatus = NutritionEntitySyncStatus.CLEAN;
  lastError = null;
  errorKind = null;
  baseRev = 0;
  localVersion = 0;
  serverBase = null;
  remoteHead = null;
  touchedPaths = [];
  timerId = null;
  shouldResaveAfterRebase = false;
  deletedRemotely = false;
  pendingServerAcks = [];

  constructor(options = {}) {
    this.entityStore = options.store ?? null;
    this.saveMode = options.saveMode ?? NutritionEntitySaveMode.MANUAL;
    this.saveDelayMs = Number(options.saveDelayMs ?? 1_200);
  }

  get hasUnsyncedChanges() {
    return (
      this.touchedPaths.length > 0 ||
      [
        NutritionEntitySyncStatus.SAVING,
        NutritionEntitySyncStatus.OFFLINE_PENDING,
        NutritionEntitySyncStatus.ERROR,
      ].includes(this.syncStatus)
    );
  }

  get hasFatalError() {
    return (
      this.syncStatus === NutritionEntitySyncStatus.ERROR &&
      this.errorKind === SyncErrorKind.FATAL
    );
  }

  get isPersisted() {
    return Number(this._rev ?? 0) > 0;
  }

  get hasPendingDebouncedSave() {
    return (
      this.saveMode === NutritionEntitySaveMode.DEBOUNCED &&
      this.syncStatus === NutritionEntitySyncStatus.DIRTY &&
      this.timerId != null
    );
  }

  getPendingServerAck(rev) {
    const resolvedRev = Number(rev ?? 0) || 0;
    if (resolvedRev <= 0) {
      return null;
    }

    return this.pendingServerAcks.find((pendingAck) => pendingAck?.rev === resolvedRev) ?? null;
  }

  hasPendingServerAck(rev) {
    return this.getPendingServerAck(rev) != null;
  }

  clearPendingServerAcks() {
    this.pendingServerAcks = [];
  }

  clearPendingServerAck(rev) {
    const resolvedRev = Number(rev ?? 0) || 0;
    if (resolvedRev <= 0) {
      return;
    }

    this.pendingServerAcks = this.pendingServerAcks.filter(
      (pendingAck) => pendingAck?.rev !== resolvedRev,
    );
  }

  registerPendingServerAck({
    submittedSnapshot,
    submittedVersion,
    submittedComparablePayload,
    nextRev,
  } = {}) {
    const resolvedRev = Number(nextRev ?? 0) || 0;
    if (resolvedRev <= 0) {
      return null;
    }

    const nextPendingAck = {
      rev: resolvedRev,
      submittedVersion: Number(submittedVersion ?? this.localVersion ?? 0) || 0,
      submittedSnapshot: clonePlainValue(submittedSnapshot),
      submittedComparablePayload: clonePlainValue(submittedComparablePayload),
    };

    this.pendingServerAcks = this.pendingServerAcks
      .filter((pendingAck) => pendingAck?.rev !== resolvedRev)
      .concat(nextPendingAck)
      .sort((leftAck, rightAck) => leftAck.rev - rightAck.rev);

    return nextPendingAck;
  }

  buildComparablePersistedPayloadFromDraft(_snapshot = this.getDraftSnapshot(), _options = {}) {
    return null;
  }

  buildComparablePersistedPayloadFromServer(_record, _options = {}) {
    return null;
  }

  shouldKeepDraftRefForComparableSelfAck(pendingAck, { finalize = false } = {}) {
    if (!pendingAck) {
      return false;
    }

    if (!finalize || this.localVersion > pendingAck.submittedVersion) {
      return true;
    }

    const currentComparablePayload = this.buildComparablePersistedPayloadFromDraft(
      this.getDraftSnapshot(),
    );
    if (currentComparablePayload == null) {
      return false;
    }

    return areEqualByJson(
      currentComparablePayload,
      pendingAck.submittedComparablePayload,
    );
  }

  finalizeComparableSelfAckWithoutDraftRefresh({
    nextSnapshot,
    nextRev,
    pendingAck,
    finalize = false,
  } = {}) {
    this._rev = Number(nextRev ?? this._rev ?? 0) || 0;
    this.baseRev = this._rev;
    this.lastError = null;
    this.errorKind = null;
    this.shouldResaveAfterRebase = false;
    this.deletedRemotely = false;
    this.serverBase = clonePlainValue(nextSnapshot);
    this.remoteHead = null;

    if (!finalize) {
      if (this.localVersion > pendingAck.submittedVersion) {
        this.syncStatus = NutritionEntitySyncStatus.DIRTY;
        if (this.saveMode === NutritionEntitySaveMode.DEBOUNCED) {
          this.scheduleSave();
        }
      } else {
        this.syncStatus = NutritionEntitySyncStatus.SAVING;
      }
      return true;
    }

    this.clearPendingServerAck(nextRev);

    if (this.localVersion > pendingAck.submittedVersion) {
      this.syncStatus = NutritionEntitySyncStatus.DIRTY;
      if (this.saveMode === NutritionEntitySaveMode.DEBOUNCED) {
        this.scheduleSave();
      }
      return true;
    }

    this.syncStatus = NutritionEntitySyncStatus.CLEAN;
    this.serverBase = null;
    this.remoteHead = null;
    this.touchedPaths = [];
    return true;
  }

  tryProcessComparableServerAck({
    nextSnapshot,
    nextRev,
    comparablePayload,
    finalize = false,
  } = {}) {
    const pendingAck = this.getPendingServerAck(nextRev);
    if (
      !pendingAck ||
      !areEqualByJson(pendingAck?.submittedComparablePayload, comparablePayload)
    ) {
      return false;
    }

    if (!this.shouldKeepDraftRefForComparableSelfAck(pendingAck, { finalize })) {
      this._rev = Number(nextRev ?? this._rev ?? 0) || 0;
      this.baseRev = this._rev;
      this.lastError = null;
      this.errorKind = null;
      this.shouldResaveAfterRebase = false;
      this.deletedRemotely = false;
      this.draft = mergeServerIntoLocalByTouchedPathsPreservingRefs(
        nextSnapshot,
        this.draft,
        this.touchedPaths,
      );
      this.serverBase = clonePlainValue(nextSnapshot);
      this.remoteHead = null;

      if (!finalize) {
        if (this.localVersion > pendingAck.submittedVersion) {
          this.syncStatus = NutritionEntitySyncStatus.DIRTY;
          if (this.saveMode === NutritionEntitySaveMode.DEBOUNCED) {
            this.scheduleSave();
          }
        } else {
          this.syncStatus = NutritionEntitySyncStatus.SAVING;
        }
        return true;
      }

      this.clearPendingServerAck(nextRev);

      if (this.localVersion > pendingAck.submittedVersion) {
        this.syncStatus = NutritionEntitySyncStatus.DIRTY;
        if (this.saveMode === NutritionEntitySaveMode.DEBOUNCED) {
          this.scheduleSave();
        }
        return true;
      }

      this.syncStatus = NutritionEntitySyncStatus.CLEAN;
      this.serverBase = null;
      this.remoteHead = null;
      this.touchedPaths = [];
      return true;
    }

    return this.finalizeComparableSelfAckWithoutDraftRefresh({
      nextSnapshot,
      nextRev,
      pendingAck,
      finalize,
    });
  }

  finalizePendingServerAckAfterMerge(nextRev) {
    const pendingAck = this.getPendingServerAck(nextRev);
    if (!pendingAck) {
      return false;
    }

    this.clearPendingServerAck(nextRev);
    this._rev = Number(nextRev ?? this._rev ?? 0) || 0;
    this.baseRev = this._rev;
    this.lastError = null;
    this.errorKind = null;
    this.shouldResaveAfterRebase = false;
    this.deletedRemotely = false;

    if (this.localVersion > pendingAck.submittedVersion) {
      this.syncStatus = NutritionEntitySyncStatus.DIRTY;
      if (this.saveMode === NutritionEntitySaveMode.DEBOUNCED) {
        this.scheduleSave();
      }
      return true;
    }

    this.syncStatus = NutritionEntitySyncStatus.CLEAN;
    if (this.serverBase != null) {
      this.draft = clonePlainValue(this.serverBase);
    }
    this.serverBase = null;
    this.remoteHead = null;
    this.touchedPaths = [];
    return true;
  }

  initializeFromServerSnapshot(snapshot, options = {}) {
    this.replaceDraftSnapshot(snapshot);
    this._rev = Number(options.rev ?? snapshot?._rev ?? 0) || 0;
    this.baseRev = this._rev;
    this.syncStatus = NutritionEntitySyncStatus.CLEAN;
    this.lastError = null;
    this.errorKind = null;
    this.serverBase = null;
    this.remoteHead = null;
    this.touchedPaths = [];
    this.shouldResaveAfterRebase = false;
    this.deletedRemotely = false;
    this.pendingServerAcks = [];
  }

  beginLocalSession() {
    if (this.touchedPaths.length > 0 || this.serverBase != null) {
      return;
    }

    this.serverBase = clonePlainValue(this.getDraftSnapshot());
    this.baseRev = Number(this._rev ?? this.baseRev ?? 0) || 0;
  }

  markTouchedPaths(paths) {
    const nextTouchedPaths = uniquePaths(this.touchedPaths.concat(paths));

    if (nextTouchedPaths.length === this.touchedPaths.length) {
      return;
    }

    this.touchedPaths = nextTouchedPaths;
  }

  applyDraftSnapshot(nextSnapshot, options = {}) {
    const currentSnapshot = this.getDraftSnapshot();
    const changedPaths =
      options.touchedPaths != null
        ? uniquePaths(options.touchedPaths)
        : diffTopLevelPaths(currentSnapshot, nextSnapshot);

    if (changedPaths.length === 0) {
      return;
    }

    this.beginLocalSession();
    this.markTouchedPaths(changedPaths);
    this.replaceDraftSnapshot(nextSnapshot);
    this.localVersion += 1;
    this.lastError = null;
    this.errorKind = null;
    this.syncStatus = NutritionEntitySyncStatus.DIRTY;

    if (options.scheduleSave !== false) {
      this.scheduleSave();
    }
  }

  mergeServerSnapshot(nextSnapshot, options = {}) {
    const previousBaseRev = Number(this.baseRev ?? this._rev ?? 0) || 0;
    const nextRev = Number(options.rev ?? nextSnapshot?._rev ?? 0) || 0;

    if (!this.hasUnsyncedChanges) {
      this.replaceDraftSnapshot(nextSnapshot);
      this._rev = nextRev;
      this.baseRev = nextRev;
      this.syncStatus = NutritionEntitySyncStatus.CLEAN;
      this.lastError = null;
      this.errorKind = null;
      this.serverBase = null;
      this.remoteHead = null;
      this.touchedPaths = [];
      this.shouldResaveAfterRebase = false;
      this.deletedRemotely = false;
      return;
    }

    const localSnapshot = this.getDraftSnapshot();
    const mergedSnapshot = mergeServerIntoLocalByTouchedPaths(
      nextSnapshot,
      localSnapshot,
      this.touchedPaths,
    );

    this.serverBase = clonePlainValue(nextSnapshot);
    this.remoteHead = clonePlainValue(nextSnapshot);
    this.baseRev = nextRev;
    this._rev = nextRev;
    this.replaceDraftSnapshot(mergedSnapshot);
    this.deletedRemotely = false;

    if (
      this.shouldResaveAfterRebase &&
      nextRev > previousBaseRev &&
      this.saveMode === NutritionEntitySaveMode.DEBOUNCED
    ) {
      this.shouldResaveAfterRebase = false;
      this.scheduleSave();
    }
  }

  discardLocalChanges() {
    if (this.serverBase == null) {
      return;
    }

    clearTimer(this.timerId);
    this.timerId = null;
    if (this.deletedRemotely) {
      this.syncStatus = NutritionEntitySyncStatus.CLEAN;
      this.lastError = null;
      this.errorKind = null;
      this.serverBase = null;
      this.remoteHead = null;
      this.touchedPaths = [];
      this.shouldResaveAfterRebase = false;
      this.deletedRemotely = false;
      this.pendingServerAcks = [];
      this.entityStore?.discardRemoteDeletedEntity?.(this);
      return;
    }

    this.replaceDraftSnapshot(this.serverBase);
    this._rev = Number(this.baseRev ?? this._rev ?? 0) || 0;
    this.syncStatus = NutritionEntitySyncStatus.CLEAN;
    this.lastError = null;
    this.errorKind = null;
    this.serverBase = null;
    this.remoteHead = null;
    this.touchedPaths = [];
    this.shouldResaveAfterRebase = false;
    this.deletedRemotely = false;
    this.pendingServerAcks = [];
  }

  noteMutationQueued({ offline = false } = {}) {
    this.syncStatus = offline
      ? NutritionEntitySyncStatus.OFFLINE_PENDING
      : NutritionEntitySyncStatus.SAVING;
  }

  noteMutationSuccess({ submittedSnapshot, submittedVersion, nextRev }) {
    const submittedComparablePayload = this.buildComparablePersistedPayloadFromDraft(
      submittedSnapshot,
    );

    this.registerPendingServerAck({
      submittedSnapshot,
      submittedVersion,
      submittedComparablePayload,
      nextRev,
    });
    this.lastError = null;
    this.errorKind = null;
    this.shouldResaveAfterRebase = false;
    this.deletedRemotely = false;

    if (this.localVersion > submittedVersion) {
      this.serverBase = clonePlainValue(submittedSnapshot);
      this.remoteHead = null;
      this.syncStatus = NutritionEntitySyncStatus.DIRTY;
      if (this.saveMode === NutritionEntitySaveMode.DEBOUNCED) {
        this.scheduleSave();
      }
      return;
    }

    this.syncStatus = NutritionEntitySyncStatus.SAVING;
  }

  noteMutationError(error, { kind = SyncErrorKind.RETRYABLE, offline = false } = {}) {
    this.lastError = error;
    this.errorKind = kind;
    this.shouldResaveAfterRebase = false;
    this.pendingServerAcks = [];

    if (kind === SyncErrorKind.FATAL) {
      this.syncStatus = NutritionEntitySyncStatus.ERROR;
      return;
    }

    this.syncStatus = offline
      ? NutritionEntitySyncStatus.OFFLINE_PENDING
      : NutritionEntitySyncStatus.DIRTY;
  }

  noteMutationBlocked(error) {
    this.lastError = error;
    this.errorKind = SyncErrorKind.BLOCKED;
    this.syncStatus = NutritionEntitySyncStatus.DIRTY;
    this.shouldResaveAfterRebase = true;
    this.pendingServerAcks = [];
  }

  markDeletedRemotely() {
    this.baseRev = 0;
    this._rev = 0;
    this.remoteHead = null;
    this.deletedRemotely = true;
    this.pendingServerAcks = [];
  }

  scheduleSave() {
    clearTimer(this.timerId);
    this.timerId = null;

    if (
      this.saveMode !== NutritionEntitySaveMode.DEBOUNCED ||
      !this.isPersistable
    ) {
      return;
    }

    this.timerId = setTimeout(() => {
      this.timerId = null;
      this.saveNow();
    }, this.saveDelayMs);
  }

  saveNow() {
    clearTimer(this.timerId);
    this.timerId = null;
    return this.entityStore?.queueEntitySave?.(this) ?? Promise.resolve(false);
  }

  dispose() {
    clearTimer(this.timerId);
    this.timerId = null;
  }
}

export function getOptimisticEntityAnnotations() {
  return {
    _rev: observable,
    syncStatus: observable,
    lastError: observable.ref,
    errorKind: observable,
    baseRev: observable,
    localVersion: observable,
    serverBase: observable.ref,
    remoteHead: observable.ref,
    touchedPaths: observable.shallow,
    shouldResaveAfterRebase: observable,
    deletedRemotely: observable,
    pendingServerAcks: observable.shallow,
    timerId: observable.ref,
    hasPendingDebouncedSave: computed,
    isPersisted: computed,
    initializeFromServerSnapshot: action.bound,
    getPendingServerAck: action.bound,
    hasPendingServerAck: action.bound,
    clearPendingServerAcks: action.bound,
    clearPendingServerAck: action.bound,
    registerPendingServerAck: action.bound,
    shouldKeepDraftRefForComparableSelfAck: action.bound,
    finalizeComparableSelfAckWithoutDraftRefresh: action.bound,
    tryProcessComparableServerAck: action.bound,
    finalizePendingServerAckAfterMerge: action.bound,
    beginLocalSession: action.bound,
    markTouchedPaths: action.bound,
    applyDraftSnapshot: action.bound,
    mergeServerSnapshot: action.bound,
    discardLocalChanges: action.bound,
    noteMutationQueued: action.bound,
    noteMutationSuccess: action.bound,
    noteMutationError: action.bound,
    noteMutationBlocked: action.bound,
    markDeletedRemotely: action.bound,
    scheduleSave: action.bound,
  };
}

export default OptimisticNutritionEntity;
